import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

const DEFAULT_CENTER = { lat: -16.5, lng: -63.6 };
const MAX_STOPS_PER_SEGMENT = 25; // Google limit: origin + 23 waypoints + destination
const ROUTE_COLOR = "#16a34a";
const ARROW_REPEAT = "96px";
const DETAIL_ROUTE_STROKE = "#16a34a";
const HISTORICO_ROUTE_STROKE = "#dc2626";
const HISTORIC_VEHICLE_PATH_STROKE = "#dc2626";

function createPinIcon(hexColor) {
  const color = hexColor || "#2563eb";
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">
  <path fill="${color}" stroke="rgba(0,0,0,0.25)" stroke-width="1.2"
    d="M17 1.5C8.44 1.5 1.5 8.44 1.5 17c0 9.4 15.5 24.5 15.5 24.5S32.5 26.4 32.5 17C32.5 8.44 25.56 1.5 17 1.5z"/>
  <circle cx="17" cy="16" r="6" fill="white"/>
</svg>`;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(28, 36),
    anchor: new window.google.maps.Point(14, 36),
  };
}

function createNameOverlayFactory(maps) {
  function NameOverlay(map, position, text) {
    this.position = position;
    this.text = text != null ? String(text) : "";
    this.div = null;
    this.setMap(map);
  }
  NameOverlay.prototype = Object.create(maps.OverlayView.prototype);
  NameOverlay.prototype.onAdd = function onAdd() {
    if (!this.text) return;
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.pointerEvents = "none";
    div.style.transform = "translateX(-50%)";
    div.style.background = "rgba(255,255,255,0.95)";
    div.style.padding = "2px 6px";
    div.style.borderRadius = "4px";
    div.style.boxShadow = "0 1px 3px rgba(0,0,0,0.25)";
    div.style.color = "#111827";
    div.style.fontSize = "11px";
    div.style.fontWeight = "600";
    div.style.whiteSpace = "nowrap";
    div.style.maxWidth = "180px";
    div.style.overflow = "hidden";
    div.style.textOverflow = "ellipsis";
    div.textContent = this.text;
    this.div = div;
    const panes = this.getPanes();
    if (panes && panes.markerLayer) panes.markerLayer.appendChild(div);
  };
  NameOverlay.prototype.draw = function draw() {
    if (!this.div || !this.position) return;
    const projection = this.getProjection();
    if (!projection) return;
    const pixel = projection.fromLatLngToDivPixel(
      new maps.LatLng(this.position.lat, this.position.lng)
    );
    if (!pixel) return;
    this.div.style.left = `${pixel.x}px`;
    this.div.style.top = `${pixel.y + 6}px`;
  };
  NameOverlay.prototype.onRemove = function onRemove() {
    if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
    this.div = null;
  };
  return NameOverlay;
}

/**
 * @param {{ stops?: Array<any>, heightClassName?: string }} props
 */
export default function ReporteEntregasGoogleMap({
  stops = [],
  heightClassName = "h-[360px]",
  historico = null,
  kilometraje = null,
  valuePlaca = null,
  labelPlaca = null,
  fechaEntrega = null,
}) {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const labelsRef = useRef([]);
  const polylinesRef = useRef([]);
  const historicoPolylineRef = useRef(null);
  const historicoMarkersRef = useRef([]);
  const historicoInfoWindowRef = useRef(null);
  const [plannedRouteKm, setPlannedRouteKm] = useState(null);

  // useEffect(() => {
  // if (Array.isArray(historico?.data) && historico?.data?.length > 0) {
  // console.log('historico?.data');
  // console.log(historico?.data);
  // }
  // console.log('kilometraje');
  // console.log(kilometraje);
  // }, [historico, kilometraje]);

  const validStops = useMemo(
    () =>
      (stops || [])
        .map((stop) => ({
          lat: parseFloat(stop?.latitud),
          lng: parseFloat(stop?.longitud),
          color: stop?.colorCliente || "#2563eb",
          name: stop?.nombreCliente || "Cliente",
        }))
        .filter(
          (stop) =>
            !Number.isNaN(stop.lat) &&
            !Number.isNaN(stop.lng) &&
            Number.isFinite(stop.lat) &&
            Number.isFinite(stop.lng)
        ),
    [stops]
  );

  const validHistoricoPoints = useMemo(
    () =>
      (historico?.data || [])
        .map((point) => ({
          lat: parseFloat(point?.lat),
          lng: parseFloat(point?.lng),
          fechaHora: point?.fechaHora ?? null,
        }))
        .filter(
          (point) =>
            !Number.isNaN(point.lat) &&
            !Number.isNaN(point.lng) &&
            Number.isFinite(point.lat) &&
            Number.isFinite(point.lng)
        ),
    [historico]
  );

  useEffect(() => {
    if (!window.google || !window.google.maps || !mapContainerRef.current) return;
    if (mapRef.current) return;
    mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: validStops[0] || DEFAULT_CENTER,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
  }, [validStops]);

  useEffect(() => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    const map = mapRef.current;
    const maps = window.google.maps;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    labelsRef.current.forEach((l) => l.setMap(null));
    labelsRef.current = [];

    const NameOverlay = createNameOverlayFactory(maps);
    const bounds = new maps.LatLngBounds();

    validStops.forEach((stop) => {
      const marker = new maps.Marker({
        map,
        position: { lat: stop.lat, lng: stop.lng },
        icon: createPinIcon(stop.color),
      });
      markersRef.current.push(marker);
      labelsRef.current.push(new NameOverlay(map, { lat: stop.lat, lng: stop.lng }, stop.name));
      bounds.extend(new maps.LatLng(stop.lat, stop.lng));
    });

    validHistoricoPoints.forEach((point) => {
      bounds.extend(new maps.LatLng(point.lat, point.lng));
    });

    if (validStops.length > 0 || validHistoricoPoints.length > 0) {
      map.fitBounds(bounds);
      if (validStops.length + validHistoricoPoints.length === 1) map.setZoom(15);
    } else {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(7);
    }
  }, [validStops, validHistoricoPoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google || !window.google.maps) return;
    const maps = window.google.maps;

    if (historicoPolylineRef.current) {
      historicoPolylineRef.current.setMap(null);
      historicoPolylineRef.current = null;
    }
    if (historicoInfoWindowRef.current) {
      historicoInfoWindowRef.current.close();
      historicoInfoWindowRef.current = null;
    }
    historicoMarkersRef.current.forEach((marker) => marker.setMap(null));
    historicoMarkersRef.current = [];

    if (validHistoricoPoints.length < 2) return;

    historicoPolylineRef.current = new maps.Polyline({
      map,
      path: validHistoricoPoints,
      geodesic: true,
      strokeColor: HISTORICO_ROUTE_STROKE,
      strokeOpacity: 0.95,
      strokeWeight: 4,
      icons: [
        {
          icon: {
            path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3,
            strokeColor: HISTORICO_ROUTE_STROKE,
            strokeOpacity: 1,
          },
          offset: "0%",
          repeat: ARROW_REPEAT,
        },
      ],
    });

    const startPoint = validHistoricoPoints[0];
    const endPoint = validHistoricoPoints[validHistoricoPoints.length - 1];
    const startFecha = startPoint?.fechaHora
      ? moment(startPoint.fechaHora).format("YYYY-MM-DD HH:mm:ss")
      : "—";
    const endFecha = endPoint?.fechaHora
      ? moment(endPoint.fechaHora).format("YYYY-MM-DD HH:mm:ss")
      : "—";

    const startMarker = new maps.Marker({
      map,
      position: startPoint,
      icon: createPinIcon("#16a34a"),
      title: "Inicio",
    });

    const endMarker = new maps.Marker({
      map,
      position: endPoint,
      icon: createPinIcon("#dc2626"),
      title: "Fin",
    });

    const infoWindow = new maps.InfoWindow();
    startMarker.addListener("click", () => {
      infoWindow.setContent(`<div style="font-size:12px;"><strong>Inicio:</strong> ${startFecha}</div>`);
      infoWindow.open({ anchor: startMarker, map });
    });
    endMarker.addListener("click", () => {
      infoWindow.setContent(`<div style="font-size:12px;"><strong>Fin:</strong> ${endFecha}</div>`);
      infoWindow.open({ anchor: endMarker, map });
    });

    historicoInfoWindowRef.current = infoWindow;
    historicoMarkersRef.current = [startMarker, endMarker];

    return () => {
      if (historicoPolylineRef.current) {
        historicoPolylineRef.current.setMap(null);
        historicoPolylineRef.current = null;
      }
      if (historicoInfoWindowRef.current) {
        historicoInfoWindowRef.current.close();
        historicoInfoWindowRef.current = null;
      }
      historicoMarkersRef.current.forEach((marker) => marker.setMap(null));
      historicoMarkersRef.current = [];
    };
  }, [validHistoricoPoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google || !window.google.maps) return undefined;
    const maps = window.google.maps;

    polylinesRef.current.forEach((p) => p.setMap(null));
    polylinesRef.current = [];
    setPlannedRouteKm(0);

    if (validStops.length < 2) return undefined;

    let cancelled = false;
    const directionsService = new maps.DirectionsService();
    let totalMeters = 0;

    const requestRoute = (origin, destination, waypoints) =>
      new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status !== maps.DirectionsStatus.OK) {
              reject(new Error(String(status)));
              return;
            }
            resolve(result);
          }
        );
      });

    (async () => {
      try {
        for (let start = 0; start < validStops.length - 1; start += (MAX_STOPS_PER_SEGMENT - 1)) {
          if (cancelled) return;
          const endExclusive = Math.min(start + MAX_STOPS_PER_SEGMENT, validStops.length);
          const segmentStops = validStops.slice(start, endExclusive);
          if (segmentStops.length < 2) continue;

          const origin = new maps.LatLng(segmentStops[0].lat, segmentStops[0].lng);
          const destination = new maps.LatLng(
            segmentStops[segmentStops.length - 1].lat,
            segmentStops[segmentStops.length - 1].lng
          );
          const waypoints = segmentStops
            .slice(1, -1)
            .map((s) => ({ location: new maps.LatLng(s.lat, s.lng), stopover: true }));

          const result = await requestRoute(origin, destination, waypoints);
          if (cancelled) return;
          const route = result?.routes?.[0];
          if (!route) continue;

          (route.legs || []).forEach((leg) => {
            if (leg?.distance?.value) totalMeters += Number(leg.distance.value);
          });

          const polyline = new maps.Polyline({
            map,
            path: route.overview_path || [],
            geodesic: true,
            strokeColor: ROUTE_COLOR,
            strokeOpacity: 0.95,
            strokeWeight: 5,
            icons: [
              {
                icon: {
                  path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  strokeColor: ROUTE_COLOR,
                  strokeOpacity: 1,
                },
                offset: "0%",
                repeat: ARROW_REPEAT,
              },
            ],
          });
          polylinesRef.current.push(polyline);
        }

        if (cancelled) return;
        setPlannedRouteKm(totalMeters / 1000);

      } catch (err) {
        console.warn("Could not build driving route", err);
      }
    })();

    return () => {
      cancelled = true;
      polylinesRef.current.forEach((p) => p.setMap(null));
      polylinesRef.current = [];
    };
  }, [validStops]);

  return (
    <div className="relative w-full">

      <div className="absolute top-0 left-3 z-10 bg-white/95 border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs text-gray-700 mt-5">
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span
            className="inline-block w-6 h-[3px] rounded"
            style={{ backgroundColor: DETAIL_ROUTE_STROKE }}
            aria-hidden
          />
          <span>
            {t("label_ruta_planificada")}
            {plannedRouteKm != null && (
              <>
                {" "}
                <span className="font-semibold tabular-nums">
                  ({Number(plannedRouteKm).toFixed(2)}): Km
                </span>
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap mt-1">
          <span
            className="inline-block w-6 h-[3px] rounded"
            style={{ backgroundColor: HISTORIC_VEHICLE_PATH_STROKE }}
            aria-hidden
          />
          <span>
            {t("label_ruta_vehiculo")}
            {kilometraje != null && (
              <>
                {" "}
                <span className="font-semibold tabular-nums">
                  ({Number(kilometraje.data[valuePlaca].kilometrajeTotal).toFixed(2)} km)
                </span>
              </>
            )}
          </span>
        </div>
      </div>
      <div ref={mapContainerRef} className={`w-full rounded-md border border-gray-200 ${heightClassName}`} />
    </div>
  );
}
